<template>
    <v-dialog v-model="showMilestoneDialog" max-width="900" persistent scrollable transition="dialog-bottom-transition">
        <v-card class="milestone-dialog-card" elevation="12">
            <!-- Enhanced Dialog Header with Gradient -->
            <v-card-title class="dialog-header pa-2">
                <div class="header-content">
                    <div class="d-flex align-center">
                        <div class="header-icon-wrapper mr-4">
                            <v-icon color="white" size="28">mdi-flag-variant</v-icon>
                        </div>
                        <div>
                            <div class="text-h5 font-weight-bold">{{ templateName || 'Milestone Template' }}</div>
                            <div class="text-subtitle-2 opacity-90 mt-1">Project milestone overview</div>
                        </div>
                    </div>
                    <v-btn icon color="white" class="close-btn" @click="closeDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </v-card-title>

            <!-- Enhanced Stats Bar -->
            <div class="stats-bar pa-4">
                <v-row no-gutters>
                    <v-col cols="4">
                        <div class="stat-item text-center">
                            <div class="stat-number">{{ milestoneList.length }}</div>
                            <div class="stat-label">Total Milestones</div>
                        </div>
                    </v-col>
                    <v-col cols="4">
                        <div class="stat-item text-center">
                            <div class="stat-number">{{ systemGeneratedCount }}</div>
                            <div class="stat-label">System Generated</div>
                        </div>
                    </v-col>
                    <v-col cols="4">
                        <div class="stat-item text-center">
                            <div class="stat-number">{{ customCount }}</div>
                            <div class="stat-label">Custom</div>
                        </div>
                    </v-col>
                </v-row>
            </div>

            <!-- Dialog Content -->
            <v-card-text class="pa-0">
                <div class="content-wrapper">
                    <div v-if="loading" class="loading-state text-center py-12">

                    </div>

                    <!-- Enhanced Empty State -->
                    <div v-else-if="milestoneList.length === 0" class="empty-state text-center py-12">
                        <div class="empty-icon-wrapper mb-6">
                            <v-icon size="80" color="grey lighten-2">mdi-flag-outline</v-icon>
                        </div>
                        <div class="text-h5 font-weight-medium mb-3">No milestones found</div>
                        <div class="text-body-1 grey--text mb-4">
                            This template doesn't have any milestones configured yet.
                        </div>
                        <v-btn color="primary" outlined @click="$emit('create-milestone')">
                            <v-icon left>mdi-plus</v-icon>
                            Create First Milestone
                        </v-btn>
                    </div>

                    <div v-else class="milestone-list pa-4">
                        <v-card v-for="(milestone, index) in milestoneList" :key="`milestone-${index}`"
                            class="milestone-card mb-4" :class="{ 'system-milestone': milestone.is_system_generated }"
                            elevation="0" outlined>
                            <div class="milestone-card-content pa-4">
                                <v-row no-gutters align="center">
                                    <v-col cols="auto" class="mr-5">
                                        <div class="milestone-indicator">
                                            <div class="milestone-avatar-wrapper">
                                                <v-avatar :color="milestone.is_system_generated ? 'primary' : 'primary'"
                                                    size="48" class="milestone-avatar elevation-4">
                                                    <v-icon color="white" size="24">
                                                        {{ milestone.milestone_type === 'System' ? 'mdi-flag-outline' :
                                                            'mdi-flag-outline' }}

                                                    </v-icon>
                                                </v-avatar>
                                            </div>

                                        </div>
                                    </v-col>

                                    <!-- Enhanced Milestone Details -->
                                    <v-col>
                                        <div class="milestone-content">
                                            <div class="d-flex align-center mb-2">
                                                <div class="text-h6 font-weight-bold milestone-title">
                                                    {{ milestone.milestone_name }}
                                                </div>
                                                <v-chip x-small
                                                    :color="milestone.is_system_generated ? 'primary' : 'primary'"
                                                    outlined class="ml-3 type-chip">
                                                    <v-icon left x-small>
                                                        {{ milestone.milestone_type === 'System' ? 'mdi-flag-outline' :
                                                            'mdi-flag-outline' }}

                                                    </v-icon>
                                                    {{ milestone.milestone_type }}
                                                </v-chip>
                                            </div>



                                        </div>
                                    </v-col>


                                </v-row>
                            </div>


                        </v-card>
                    </div>
                </div>
            </v-card-text>

            <v-divider></v-divider>
            <v-card-actions class="enhanced-actions pa-6">

            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'MilestoneDialog',
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        templateId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            milestoneList: [],
            templateName: '',
            error: null,
        };
    },
    computed: {
        showMilestoneDialog: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            },
        },
        systemGeneratedCount() {
            return this.milestoneList.filter(m => m.milestone_type === "System").length;
        },
        customCount() {
            return this.milestoneList.filter(m => m.milestone_type !== "System").length;
        },

    },
    watch: {
        value(newVal) {
            if (newVal && this.templateId) {
                this.getMilestoneTemplateObject();
            }
        },
        templateId(newVal) {
            if (newVal && this.value) {
                this.getMilestoneTemplateObject();
            }
        },
    },
    methods: {
        async getMilestoneTemplateObject() {
            if (!this.templateId) {
                console.warn('No template ID provided');
                return;
            }

            this.loading = true;
            this.error = null;

            try {
                const response = await this.$api.milestoneTemplate.getMilestoneTemplateObject(this.templateId);

                if (response.data) {
                    // Sort milestones by sequence
                    this.milestoneList = (response.data.milestone_template || []).sort(
                        (a, b) => (a.sequence || 0) - (b.sequence || 0)
                    );
                    this.templateName = response.data.name || 'Unnamed Template';
                } else {
                    this.milestoneList = [];
                    this.templateName = '';
                }
            } catch (error) {
                console.error('Error fetching milestone template:', error);
                this.error = 'Failed to load milestone data';
                this.showErrorMessage();
            } finally {
                this.loading = false;
            }
        },

        closeDialog() {
            this.showMilestoneDialog = false;
        },

        refreshMilestones() {
            this.getMilestoneTemplateObject();
        },

        viewMilestone(milestone) {
            this.$emit('view-milestone', milestone);
        },

        editMilestone(milestone) {
            this.$emit('edit-milestone', milestone);
        },

        duplicateMilestone(milestone) {
            this.$emit('duplicate-milestone', milestone);
        },

        exportMilestones() {
            this.$emit('export-milestones', this.milestoneList);
        },

        formatDate(dateString) {
            if (!dateString) return '';
            try {
                return new Date(dateString).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                });
            } catch {
                return dateString;
            }
        },

        showErrorMessage() {
            if (this.$toast) {
                this.$toast.error(this.error || 'An error occurred');
            } else {
                this.$bus.$emit('showSnackbar', {
                    text: this.error || 'An error occurred',
                    color: 'error'
                });
            }
        },
    },
};
</script>

<style scoped>
/* Dialog Card Enhancements */
.milestone-dialog-card {
    border-radius: 12px !important;
    overflow: hidden;
}

/* Enhanced Header with Gradient */
.dialog-header {
    background: linear-gradient(135deg, #db5d08 0%, #f1a008 100%);
    color: white;
    position: relative;
    overflow: hidden;
}

.dialog-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
    opacity: 0.3;
}

.header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    position: relative;
    z-index: 1;
}

.header-icon-wrapper {
    background: rgba(226, 106, 7, 0.877);
    border-radius: 12px;
    padding: 8px;
    backdrop-filter: blur(10px);
}

.close-btn {
    transition: all 0.3s ease;
}

.close-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
}

/* Enhanced Stats Bar */
.stats-bar {
    background: linear-gradient(90deg, #f8f9ff 0%, #e8f2ff 100%);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.stat-item {
    padding: 8px;
}

.stat-number {
    font-size: 24px;
    font-weight: 700;
    color: #2c3e50;
    line-height: 1;
}

.stat-label {
    font-size: 12px;
    font-weight: 500;
    color: #7f8c8d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 4px;
}

/* Content Wrapper */
.content-wrapper {
    max-height: 60vh;
    overflow-y: auto;
}

/* Enhanced Loading State */
.loading-state {
    background: linear-gradient(45deg, #f8f9ff 25%, transparent 25%),
        linear-gradient(-45deg, #f8f9ff 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #f8f9ff 75%),
        linear-gradient(-45deg, transparent 75%, #f8f9ff 75%);
    background-size: 20px 20px;
    background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
    animation: loadingBg 2s linear infinite;
}

@keyframes loadingBg {
    0% {
        background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
    }

    100% {
        background-position: 20px 20px, 20px 30px, 30px 10px, 10px 20px;
    }
}

.loading-animation {
    position: relative;
}

.loading-animation::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
    animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {

    0%,
    100% {
        transform: translate(-50%, -50%) scale(0.8);
        opacity: 1;
    }

    50% {
        transform: translate(-50%, -50%) scale(1.2);
        opacity: 0.3;
    }
}

/* Enhanced Empty State */
.empty-state {
    background: radial-gradient(circle at center, rgba(102, 126, 234, 0.05) 0%, transparent 70%);
}

.empty-icon-wrapper {
    position: relative;
}

.empty-icon-wrapper::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(158, 158, 158, 0.1) 0%, transparent 70%);
}

/* Enhanced Milestone Cards */
.milestone-list {
    background: #fafbfc;
}

.milestone-card {
    border-radius: 12px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid transparent;
    background: white;
    position: relative;
    overflow: hidden;
}

.milestone-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.milestone-card.system-milestone::before {
    background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
}

.milestone-card:not(.system-milestone)::before {
    background: linear-gradient(180deg, #48bb78 0%, #38a169 100%);
}

.milestone-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
    border-color: rgba(102, 126, 234, 0.3);
}

.milestone-card:hover::before {
    opacity: 1;
}

.milestone-card-content {
    position: relative;
    z-index: 1;
}

/* Enhanced Milestone Indicator */
.milestone-indicator {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.milestone-avatar-wrapper {
    position: relative;
}

.milestone-avatar {
    transition: all 0.3s ease;
}

.milestone-card:hover .milestone-avatar {
    transform: scale(1.1);
}

.milestone-number {
    margin-top: 8px;
    font-weight: 600;
    min-width: 32px;
}

/* Enhanced Content */
.milestone-content {
    flex: 1;
}

.milestone-title {
    color: #2c3e50;
    transition: color 0.3s ease;
}

.milestone-card:hover .milestone-title {
    color: #667eea;
}

.milestone-description {
    color: #5a6c7d;
    line-height: 1.5;
    max-width: 500px;
}

.milestone-meta {
    gap: 8px;
}

.type-chip {
    border-radius: 20px !important;
}

/* Progress Bar */
.milestone-progress-bar {
    height: 3px;
    background: #f1f3f4;
    position: relative;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    width: 100%;
    transform: translateX(-100%);
}

.primary-progress {
    background: linear-gradient(90deg, #667eea, #764ba2);
}

.success-progress {
    background: linear-gradient(90deg, #48bb78, #38a169);
}



.action-btn {
    border-radius: 8px;
    transition: all 0.3s ease;
}

.action-btn:hover {
    background: rgba(102, 126, 234, 0.1);
    transform: scale(1.1);
}

.action-menu {
    border-radius: 8px !important;
    overflow: hidden;
}

.action-item {
    transition: background-color 0.2s ease;
}

.action-item:hover {
    background: rgba(102, 126, 234, 0.05);
}

.enhanced-actions {
    background: linear-gradient(90deg, #f8f9ff 0%, #e8f2ff 100%);
}

.refresh-btn {
    border-radius: 8px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0.5px;
}

.content-wrapper::-webkit-scrollbar {
    width: 6px;
}

.content-wrapper::-webkit-scrollbar-track {
    background: #f1f3f4;
    border-radius: 3px;
}

.content-wrapper::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #667eea, #764ba2);
    border-radius: 3px;
}

.content-wrapper::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, #5a67d8, #6b46c1);
}

@media (max-width: 600px) {
    .milestone-card-content {
        padding: 16px !important;
    }

    .milestone-indicator {
        margin-right: 16px !important;
    }

    .stat-number {
        font-size: 20px;
    }

    .milestone-title {
        font-size: 16px !important;
    }
}

.milestone-card {
    animation: slideIn 0.3s ease-out;
}

.milestone-card:nth-child(odd) {
    animation-delay: 0.1s;
}

.milestone-card:nth-child(even) {
    animation-delay: 0.2s;
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.action-btn:focus,
.refresh-btn:focus,
.close-btn:focus {
    outline: 2px solid #667eea;
    outline-offset: 2px;
}
</style>